// API Keys management for scheduling platform
import React, { useState, useEffect } from 'react';

// API Key interface
interface APIKey {
  id: number;
  name: string;
  keyPrefix: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string | null;
  isActive: boolean;
}

// Props for the component
interface APIKeysProps {
  plan: 'free' | 'pro';
  onUpgrade?: () => void;
}

const API_BASE = '/backend/scheduling';

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const APIKeys: React.FC<APIKeysProps> = ({ plan, onUpgrade }) => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['read']);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (plan === 'pro') {
      loadAPIKeys();
    } else {
      setLoading(false);
    }
  }, [plan]);

  const loadAPIKeys = async () => {
    try {
      const response = await fetch(`${API_BASE}/api-keys`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load API keys');
      }

      const data = await response.json();
      setApiKeys(data.apiKeys || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    if (newKeyPermissions.length === 0) {
      setError('Please select at least one permission');
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${API_BASE}/api-keys`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newKeyName,
          permissions: newKeyPermissions
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create API key');
      }

      const data = await response.json();
      setCreatedKey(data.apiKey);
      setShowCreateForm(false);
      setNewKeyName('');
      setNewKeyPermissions(['read']);

      // Reload keys list
      await loadAPIKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(`${API_BASE}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }

      setApiKeys(apiKeys.filter(key => key.id !== keyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const handleCopyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePermissionToggle = (permission: string) => {
    if (newKeyPermissions.includes(permission)) {
      setNewKeyPermissions(newKeyPermissions.filter(p => p !== permission));
    } else {
      setNewKeyPermissions([...newKeyPermissions, permission]);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Free plan upgrade prompt
  if (plan !== 'pro') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          maxWidth: '500px',
          margin: '0 auto',
          padding: '40px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 20px',
            backgroundColor: '#fef3c7',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px'
          }}>
            🔑
          </div>
          <h2 style={{ margin: '0 0 10px', fontSize: '1.5rem' }}>API Access</h2>
          <p style={{ margin: '0 0 20px', color: '#666', lineHeight: '1.6' }}>
            Integrate your scheduling with custom applications using our REST API.
            Generate API keys to authenticate your requests.
          </p>
          <p style={{ margin: '0 0 30px', color: '#666' }}>
            <strong>Included in the Supporters Club ($10/year)</strong>
          </p>
          <button
            onClick={onUpgrade}
            style={{
              padding: '12px 30px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '1rem'
            }}
          >
            Join Supporters Club
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading API keys...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem' }}>API Keys</h1>
          <p style={{ margin: '5px 0 0', color: '#666' }}>
            Manage your API keys for programmatic access
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          + New API Key
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {/* Created Key Display (show once) */}
      {createdKey && (
        <div style={{
          padding: '20px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          marginBottom: '30px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <span style={{ fontSize: '1.5rem' }}>✅</span>
            <h3 style={{ margin: 0, color: '#166534' }}>API Key Created</h3>
          </div>
          <p style={{
            margin: '0 0 15px',
            padding: '12px 15px',
            backgroundColor: 'white',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            wordBreak: 'break-all',
            border: '1px solid #d1d5db'
          }}>
            {createdKey}
          </p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleCopyKey}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {copied ? 'Copied!' : 'Copy Key'}
            </button>
            <button
              onClick={() => setCreatedKey(null)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
          <p style={{
            margin: '15px 0 0',
            padding: '10px',
            backgroundColor: '#fef3c7',
            borderRadius: '6px',
            color: '#854d0e',
            fontSize: '0.875rem'
          }}>
            ⚠️ Save this key now. You won't be able to see it again.
          </p>
        </div>
      )}

      {/* API Keys List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '30px'
      }}>
        <div style={{
          padding: '15px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          borderRadius: '12px 12px 0 0'
        }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>Active Keys</h2>
        </div>

        {apiKeys.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <p style={{ margin: 0 }}>No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <div>
            {apiKeys.map(key => (
              <div
                key={key.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px 20px',
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: '500' }}>{key.name}</p>
                  <p style={{
                    margin: '5px 0 0',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: '#666'
                  }}>
                    {key.keyPrefix}...
                  </p>
                  <p style={{ margin: '5px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                    Created: {formatDate(key.createdAt)}
                    {key.lastUsed && ` | Last used: ${formatDate(key.lastUsed)}`}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    {key.permissions.map(perm => (
                      <span
                        key={perm}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: perm === 'read' ? '#dcfce7' : perm === 'write' ? '#dbeafe' : '#fef3c7',
                          color: perm === 'read' ? '#166534' : perm === 'write' ? '#1e40af' : '#854d0e',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => handleRevokeKey(key.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: '#dc2626',
                      fontSize: '0.875rem'
                    }}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Documentation Snippet */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          padding: '15px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          borderRadius: '12px 12px 0 0'
        }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>Quick Start</h2>
        </div>
        <div style={{ padding: '20px' }}>
          <p style={{ margin: '0 0 15px', color: '#374151' }}>
            Use your API key in the Authorization header:
          </p>
          <pre style={{
            margin: 0,
            padding: '15px',
            backgroundColor: '#1e293b',
            color: '#e2e8f0',
            borderRadius: '8px',
            overflow: 'auto',
            fontSize: '0.875rem',
            lineHeight: '1.6'
          }}>
{`curl -X GET "https://cal.larryshelp.org/api/v1/scheduling/bookings" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          </pre>
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.875rem', fontWeight: '500' }}>Available Endpoints</h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>GET</span>
                <code style={{ fontSize: '0.875rem', color: '#374151' }}>/api/v1/scheduling/availability</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>GET</span>
                <code style={{ fontSize: '0.875rem', color: '#374151' }}>/api/v1/scheduling/event-types</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>GET</span>
                <code style={{ fontSize: '0.875rem', color: '#374151' }}>/api/v1/scheduling/bookings</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>POST</span>
                <code style={{ fontSize: '0.875rem', color: '#374151' }}>/api/v1/scheduling/bookings/create</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>POST</span>
                <code style={{ fontSize: '0.875rem', color: '#374151' }}>/api/v1/scheduling/bookings/{'{uid}'}/cancel</code>
              </div>
            </div>
          </div>
          <a
            href="https://larryshelp.org/docs/scheduling-api"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '20px',
              color: '#3b82f6',
              fontSize: '0.875rem'
            }}
          >
            View full API documentation →
          </a>
        </div>
      </div>

      {/* Create Key Modal */}
      {showCreateForm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-key-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowCreateForm(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowCreateForm(false); } }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '450px',
              width: '90%'
            }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <h3 id="create-key-title" style={{ margin: '0 0 20px' }}>Create API Key</h3>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="api-key-name" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Key Name
              </label>
              <input
                id="api-key-name"
                type="text"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder="e.g., Production, Development, Testing"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <fieldset style={{ marginBottom: '20px', border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                Permissions
              </legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label htmlFor="perm-read" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    id="perm-read"
                    type="checkbox"
                    checked={newKeyPermissions.includes('read')}
                    onChange={() => handlePermissionToggle('read')}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>
                    <strong>Read</strong>
                    <span style={{ color: '#666', fontSize: '0.875rem' }}> - View bookings, availability, event types</span>
                  </span>
                </label>
                <label htmlFor="perm-write" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    id="perm-write"
                    type="checkbox"
                    checked={newKeyPermissions.includes('write')}
                    onChange={() => handlePermissionToggle('write')}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>
                    <strong>Write</strong>
                    <span style={{ color: '#666', fontSize: '0.875rem' }}> - Create, update, cancel bookings</span>
                  </span>
                </label>
                <label htmlFor="perm-payments" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    id="perm-payments"
                    type="checkbox"
                    checked={newKeyPermissions.includes('payments')}
                    onChange={() => handlePermissionToggle('payments')}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>
                    <strong>Payments</strong>
                    <span style={{ color: '#666', fontSize: '0.875rem' }}> - Create payment intents, view payment status</span>
                  </span>
                </label>
              </div>
            </fieldset>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKeyName('');
                  setNewKeyPermissions(['read']);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                disabled={!newKeyName.trim() || newKeyPermissions.length === 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (newKeyName.trim() && newKeyPermissions.length > 0) ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (newKeyName.trim() && newKeyPermissions.length > 0) ? 'pointer' : 'not-allowed'
                }}
              >
                Generate Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeys;
